import { useMemo, useCallback } from "react";
import classNames from "classnames";
import queryString from "query-string";
import BaseButton from "../../../../components/common/BaseButton";
import { useMediaQuery } from "react-responsive";
import { TO_MEDIUM } from "core/ui/breakpoints";
import { useHistory } from "react-router-dom";
import { NextIcon, PrevIcon } from "./icons/Icons";

interface PaginationProps {
  className?: string;
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({
  className,
  currentPage,
  pageCount,
  onPageChange,
}: PaginationProps) => {
  const isMediumScreen = useMediaQuery({ query: TO_MEDIUM });
  const history = useHistory();

  if (pageCount <= 1) return null;

  const getPageRange = (
    pageCount: number,
    currentPage: number,
    maxPages: number
  ): { first: number; last: number } => {
    if (pageCount <= maxPages) {
      return { first: 1, last: pageCount };
    }

    const halfPages = Math.floor(maxPages / 2);
    let first = currentPage - halfPages;
    let last = currentPage + halfPages;

    if (first < 1) {
      first = 1;
      last = maxPages;
    } else if (last > pageCount) {
      last = pageCount;
      first = pageCount - maxPages + 1;
    }

    return { first, last };
  };

  const maxPages = isMediumScreen ? 5 : 9;
  const { first, last } = getPageRange(pageCount, currentPage, maxPages);

  const pages = useMemo(
    () => Array.from({ length: last - first + 1 }, (_, idx) => first + idx),
    [first, last]
  );

  const updateURL = useCallback(
    (newPage: number) => {
      const currentQuery = queryString.parse(history.location.search);
      if (newPage === 1) {
        delete currentQuery.page;
      } else {
        currentQuery.page = String(newPage);
      }
      const newSearch =
        Object.keys(currentQuery).length > 0
          ? `?${queryString.stringify(currentQuery)}`
          : "";
      history.push(history.location.pathname + newSearch);
    },
    [history]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateURL(newPage);
      onPageChange(newPage);
    },
    [updateURL, onPageChange]
  );

  const pageButtons = useMemo(
    () =>
      pages.map((page) => (
        <BaseButton
          key={`pagination-page-${page}`}
          className={classNames("pagination__page--button", {
            "current-page": page === currentPage,
          })}
          text={String(page)}
          disabled={page === currentPage}
          handleSubmit={() => handlePageChange(page)}
        />
      )),
    [pages, currentPage, handlePageChange]
  );

  return (
    <div className={classNames("pagination", className)}>
      <BaseButton
        className="pagination__prev--button button--has-icon"
        disabled={currentPage === first}
        handleSubmit={() => handlePageChange(currentPage - 1)}
        iconComponent={<PrevIcon />}
      />
      <div className="pagination__filler" />
      <div className="pagination__pages-container">{pageButtons}</div>
      <div className="pagination__filler" />
      <BaseButton
        className="pagination__next--button button--has-icon"
        disabled={currentPage === last}
        handleSubmit={() => handlePageChange(currentPage + 1)}
        iconComponent={<NextIcon />}
      />
    </div>
  );
};

export default Pagination;
